import { useUpdateBusinessProfile, useUpdateProfile } from "@hooks/queries"
import { displayErrorAlert, useAlertOverlay } from "@hooks/useAlertOverlay"
import { useEffect, useState } from "react"
import { UserOrBusinessProfile } from "../profile-type"
import { PROFILE_EDIT_FORMS, ProfileEditFormType } from "../utils/profile-editor"

export function useProfileEditor(props: { data: UserOrBusinessProfile; onChange: (data: UserOrBusinessProfile) => void }) {
  const alert = useAlertOverlay()

  return {
    open(type: keyof typeof PROFILE_EDIT_FORMS) {
      let newData = { ...props.data }
      let isValid = true
      const TYPE = PROFILE_EDIT_FORMS[type]
      return alert.open({
        title: TYPE.label,
        message: (
          <EditorRender
            type={TYPE}
            getData={() => newData}
            onChange={(data) => (newData = data)}
            onValid={(valid) => (isValid = valid)}
            onSubmit={() => {
              props.onChange(newData)
              alert.close()
            }}
          />
        ),
        confirmLabel: "Save",
        canCancel: true,
        cancelLabel: "Cancel",
        className: "md:max-w-none md:w-auto",
        asForm: true,
        onConfirm: async () => {
          if (!isValid) {
            return false
          }
          try {
            if (TYPE.onSave) {
              const result = await TYPE.onSave(newData)
              props.onChange(result)
            } else {
              if (props.data.__typename === "Business") {
                const result = await useUpdateBusinessProfile.mutate({
                  data: JSON.stringify({
                    ...newData.publicProfileData,
                    ...(newData.__typename === "Business" ? newData.businessData : {}),
                  }),
                })
                props.onChange(result.updateMyBusinessProfile?.business!)
              } else {
                const result = await useUpdateProfile.mutate({
                  data: JSON.stringify(newData.publicProfileData),
                })
                props.onChange(result.updateMyProfile?.user!)
              }
            }
          } catch (err) {
            displayErrorAlert(err)
          }
        },
      })
    },
  }
}

type EditorProps = {
  type: ProfileEditFormType<any>
  getData: () => UserOrBusinessProfile
  onChange: (data: UserOrBusinessProfile) => void
  onSubmit: () => void
  onValid: (valid: boolean) => void
}

function EditorRender(props: EditorProps) {
  const [errors, setErrors] = useState(null)

  const [profile, setProfile] = useState(props.getData)
  const Form = props.type.editor

  useEffect(() => {
    const result = props.type.validate(profile)
    if (result.valid === false) {
      setErrors(result.errors)
      props.onValid(false)
    } else {
      setErrors(null)
      props.onValid(true)
    }
  }, [profile])

  return (
    <Form
      data={profile}
      onChange={(data) => {
        setProfile(data)
        props.onChange(data)
      }}
      errors={errors}
    />
  )
}
